<template>
  <Toast ref="toast" />
  <h1>{{ getNameArtFromId(route.params.id) }}</h1>
  <DataTable :value="tableData" responsiveLayout="scroll">
    <Column field="qlt" header="Качество">
      <template #body="{ data }">
        <span :style="`color: ${getColorQlt(data.qlt)}`">{{ data.qlt }}</span>
      </template>
    </Column>
    <Column field="ptn" header="Заточка"></Column>
    <Column field="averagePrice" header="Медиана">
      <template #body="{ data }">
        {{ formatPrice(data.averagePrice) }}
      </template>
    </Column>
    <Column field="historyMinPrice" header="Исторический минимум">
      <template #body="{ data }">
        {{ formatPrice(data.historyMinPrice) }}
      </template>
    </Column>
    <Column field="currentPrice" header="Текущий минимум">
      <template #body="{ data }">
        <span :style="`color: ${data.averagePrice > data.currentPrice ? 'green' : 'red'}`">
          {{ formatPrice(data.currentPrice) }}
        </span>
      </template>
    </Column>
    <Column field="salePrice" header="Цена продажи">
      <template #body="{ data }">
        <span @click="copyToClipboard(data.salePrice)">{{
          formatPrice(Number(data.salePrice))
        }}</span>
      </template>
    </Column>
    <Column field="currentElems" header="Лоты">
      <template #body="{ data }">
        {{ data.currentElems.map(i => i.price) }}
      </template>
    </Column>

    <Column header="Ликвидность 48ч">
      <template #body="{ data }">
        {{ calcLiquidity48h(data.historySalePrices) }}
      </template>
    </Column>

    <Column field="historySalePrices" header="Количество проданных">
      <template #body="{ data }">
        <div>
          {{ data.historySalePrices.length }}
        </div>
        <div>
          <HistoryChart :historySalePrices="data.historySalePrices" :sale-price="data.salePrice" />
        </div>
      </template>
    </Column>
    <Column field="profit" header="Профит"></Column>
  </DataTable>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { formatPrice } from '@/common/function'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import { getNameArtFromId } from '@/common/list'
import { copyText } from 'vue3-clipboard'
import Toast from 'primevue/toast'
import HistoryChart from '@/components/HistoryChart.vue'
import axios from 'axios'
import { api } from '@/api/http'

const route = useRoute()

const toast = ref()

const qltMap = {
  0: 'white',
  1: 'green',
  2: 'blue',
  3: 'purple',
  4: 'red',
  5: 'gold'
}

const getColorQlt = (qlt: number | undefined) => qltMap[qlt ?? 0]

interface Additional {
  ptn: number
  qlt: number
}

interface ItemHistory {
  amount: number
  price: number
  time: string
  additional: Additional
}

interface Item {
  itemId: string
  amount: number
  startPrice: number
  buyoutPrice: number
  startTime: string
  endTime: string
  history: ItemHistory[]
  additional: Additional
  averagePrice: number
  priceDifference: number
}

const tableData = ref([])

const copyingNotification = (text: string) => {
  toast.value?.add({
    severity: 'info',
    summary: 'Скопировано',
    detail: text,
    life: 3000
  })
}

const copyToClipboard = (text: string) => {
  copyingNotification(text)
  copyText(text, undefined, (error: Error) => {
    if (error) console.error('Copy error:', error)
  })
}

function calcLiquidity48h(history): number {
  if (!history?.length) return 0

  const now = Date.now()
  const WINDOW = 48 * 60 * 60 * 1000

  const times = history.map(h => new Date(h.time).getTime()).sort((a, b) => a - b)

  // ===== 1. продажи за 48ч =====
  const last48h = times.filter(t => t >= now - WINDOW).length

  // 6 продаж за 48ч = потолок
  const salesScore = Math.min(last48h / 6, 1) * 60

  // ===== 2. средний gap =====
  let avgGap = WINDOW

  if (times.length > 1) {
    let sum = 0
    for (let i = 1; i < times.length; i++) {
      sum += times[i] - times[i - 1]
    }
    avgGap = sum / (times.length - 1)
  }

  // 6ч → отлично, 48ч → 0
  const gapScore = Math.max(0, 1 - avgGap / WINDOW) * 25

  // ===== 3. свежесть =====
  const lastSaleAgo = now - times[times.length - 1]

  // <6ч отлично, >48ч мёртв
  const recencyScore = Math.max(0, 1 - lastSaleAgo / WINDOW) * 15

  return Math.round(Math.min(100, salesScore + gapScore + recencyScore))
}

onMounted(async () => {
  try {
    const { data } = await api.get(`/artefact/${route.params.id}`)

    tableData.value = data.filter((i: any) => !i.isBid)
  } catch (err: unknown) {
    if (axios.isAxiosError(err)) {
      console.error('Ошибка загрузки артефакта:', err.response?.data?.message)
    } else {
      console.error('Ошибка загрузки артефакта:', err)
    }
  }
})
</script>

<style lang="scss">
body {
  background-color: #1e1e1e;
  color: #f0e6d2;
  font-family: 'Arial', sans-serif;
  margin: 0;
  padding: 0;
}

.auction-table {
  width: 80%;
  margin: 20px auto;
  background-color: #2c2c2c;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.4);
}
</style>
